/**
 * Branded 404 page with optional "Did you mean?" suggestion from route similarity.
 */

import React, { useMemo, useEffect } from 'react';
import { TraceKitConfig } from '../../types/config';
import { getSuggestedPath } from '../../utils/routeSuggestions';
import { Button } from './Button';

export interface NotFoundPageProps {
  config: TraceKitConfig;
  /** Current path that was not found (without query string). */
  currentPath: string;
  /** Called when user navigates (e.g. Go Home or suggested link). */
  onNavigate: (path: string) => void;
  /** All known app routes (from App, so suggestions use the same list as route matching). */
  knownPaths?: string[];
}

const gray600 = '#4b5563';
const gray700 = '#374151';
const gray800 = '#1f2937';
const gray900 = '#111827';

export function NotFoundPage({ config, currentPath, onNavigate, knownPaths }: NotFoundPageProps) {
  const appName = config.branding?.appName ?? 'App';
  const primaryColor = config.branding?.primaryColor ?? '#1e40af';
  const logoUrl = config.branding?.logoUrl ?? config.branding?.logo;

  const suggestedPath = useMemo(
    () => getSuggestedPath(currentPath, knownPaths?.length ? { paths: knownPaths } : undefined),
    [currentPath, knownPaths]
  );

  // Auto-redirect if there's a high-confidence suggestion
  useEffect(() => {
    if (suggestedPath && suggestedPath !== currentPath) {
      console.log('[NotFoundPage] Auto-redirecting from', currentPath, 'to', suggestedPath);
      onNavigate(suggestedPath);
    }
  }, [suggestedPath, currentPath, onNavigate]);

  return (
    <div
      className="flex flex-col items-center justify-center min-h-[60vh] py-16 px-6"
      style={{
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div className="flex flex-col items-center max-w-lg w-full text-center">
        {logoUrl && (
          <img
            src={logoUrl}
            alt={config.branding?.logoAlt ?? `${appName} logo`}
            className="h-10 object-contain mb-6"
            style={{ maxWidth: 180 }}
          />
        )}
        <div
          className="text-7xl font-bold mb-2 tabular-nums"
          style={{ color: primaryColor, lineHeight: 1 }}
        >
          404
        </div>
        <h1
          className="text-xl font-semibold mb-2"
          style={{ color: gray900 }}
        >
          Page not found
        </h1>
        <p
          className="text-base mb-6"
          style={{ color: gray600 }}
        >
          The page <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800" style={{ color: gray700 }}>{currentPath || '/'}</code> doesn&apos;t exist or you don&apos;t have access to it.
        </p>

        {suggestedPath != null && suggestedPath !== currentPath && (
          <p className="text-base mb-6" style={{ color: gray700 }}>
            Did you mean{' '}
            <button
              type="button"
              onClick={() => onNavigate(suggestedPath)}
              className="font-medium underline focus:outline-none focus:ring-2 focus:ring-offset-2 rounded"
              style={{
                color: primaryColor,
                textDecoration: 'underline',
              }}
            >
              {suggestedPath === '/' ? 'Home' : suggestedPath}
            </button>
            ?
          </p>
        )}

        <div className="flex flex-wrap gap-3 justify-center">
          <Button
            variant="primary"
            onClick={() => onNavigate('/')}
          >
            Go to Home
          </Button>
          <Button
            variant="outline"
            onClick={() => window.history.back()}
          >
            Go back
          </Button>
        </div>
      </div>
    </div>
  );
}
